<template>
  <v-container data-app grid-list-xl fluid class="pa-0 ma-0">
    <v-layout row wrap>
      <v-flex>
        <BaseWidget title="Notas Fiscais Importadas">
          <v-card flat class="pa-4">

            <!-- Filtro e botão -->
            <v-row align="center" class="mb-4">
              <v-col cols="12" sm="8">
                <v-text-field
                  v-model="filtroChave"
                  label="Buscar por chave da nota fiscal"
                  solo
                  clearable
                  dense
                  prepend-inner-icon="mdi-magnify"
                  hint="Digite a chave (busca em tempo real)"
                />
              </v-col>
              <v-col cols="12" sm="4" class="text-right">
                <v-btn
                  color="primary"
                  elevation="1"
                  @click="buscarNotas"
                  class="mt-1"
                  style="background-color: #1d4ed8;"
                >
                  <v-icon left>mdi-magnify</v-icon>
                  Buscar Novamente
                </v-btn>
              </v-col>
            </v-row>

            <!-- Loader enquanto carrega -->
            <div v-if="carregando" class="text-center my-5">
              <v-progress-circular
                indeterminate
                color="primary"
                size="50"
                width="4"
              />
              <div class="mt-2 grey--text text--darken-1">Carregando notas fiscais...</div>
            </div>

            <!-- Tabela + Paginação -->
            <div v-else>
              <v-data-table
                :headers="headers"
                :items="notas"
                :items-per-page="tamanhoPagina"
                :page.sync="pagina"
                class="elevation-1"
                dense
                hover
                hide-default-footer
              >
                <template #item.valorCarga="{ item }">
                  R$ {{ item.valorCarga.toFixed(2).replace('.', ',') }}
                </template>

                <template #item.actions="{ item }">
                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-btn
                        style="color: red;"
                        icon
                        color="red"
                        v-bind="attrs"
                        v-on="on"
                        @click="abrirConfirmacao(item)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                    <span>Excluir nota fiscal</span>
                  </v-tooltip>
                </template>
              </v-data-table>

              <v-pagination
                v-model="pagina"
                :length="totalPaginas"
                @input="buscarNotas"
                class="mt-4"
                color="primary"
              />
            </div>

            <!-- Modal de confirmação -->
            <v-dialog v-model="modalExcluir" max-width="480">
              <v-card>
                <v-card-title class="headline grey--text text--darken-2">
                  <v-icon left color="warning">mdi-alert-circle</v-icon>
                  Confirmar exclusão
                </v-card-title>
                <v-card-text class="mt-2">
                  Tem certeza que deseja excluir esta nota fiscal?
                  <br />
                  <strong class="red--text text--darken-2">
                    Esta ação é irreversível.
                  </strong>
                </v-card-text>
                <v-card-actions class="justify-end">
                  <v-btn text color="grey darken-1" @click="modalExcluir = false">
                    <v-icon left>mdi-cancel</v-icon>
                    Cancelar
                  </v-btn>
                  <v-btn color="red darken-1" dark @click="confirmarExclusao">
                    <v-icon left>mdi-delete</v-icon>
                    Excluir
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>

          </v-card>
        </BaseWidget>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import EmissaoViagem from "../service/emissaoViagem";

export default {
  data() {
    return {
      filtroChave: '',
      pagina: 1,
      tamanhoPagina: 15,
      totalPaginas: 1,
      notas: [],
      notasCompleta: [],
      notaSelecionada: null,
      modalExcluir: false,
      carregando: false,
      timeoutBusca: null,
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Chave', value: 'chave' },
        { text: 'Viagem', value: 'emissaoViagem.id' },
        { text: 'Remetente', value: 'remetente.razaoSocial' },
        { text: 'Destinatário', value: 'destinatario.razaoSocial' },
        { text: 'Valor', value: 'valorCarga' },
        { text: 'Ações', value: 'actions', sortable: false },
      ]
    };
  },
  mounted() {
    this.buscarNotas();
  },
  watch: {
    filtroChave() {
      // Limpa timeout anterior
      if (this.timeoutBusca) {
        clearTimeout(this.timeoutBusca);
      }
      
      // Reseta para primeira página
      this.pagina = 1;
      
      const filtro = this.filtroChave.trim().toLowerCase();
      
      // Se campo vazio, mostra todas as notas
      if (filtro.length === 0) {
        this.notas = this.notasCompleta;
        this.totalPaginas = Math.ceil(this.notasCompleta.length / this.tamanhoPagina);
        return;
      }
      
      // Debounce de 300ms para filtrar
      this.timeoutBusca = setTimeout(() => {
        // Filtra as notas baseado na chave digitada
        this.notas = this.notasCompleta.filter(nota => 
          nota.chave && nota.chave.toLowerCase().includes(filtro)
        );
        
        // Calcula paginação dos resultados filtrados
        this.totalPaginas = Math.ceil(this.notas.length / this.tamanhoPagina);
        this.pagina = 1;
      }, 300);
    }
  },
  methods: {
    async buscarNotas() {
      this.carregando = true;
      try {
        // Carrega todas as notas (sem filtro)
        const response = await EmissaoViagem.getNotasFiscais(
          '',
          1,
          1000 // Carrega muitas para ter sempre disponível para filtro
        );
        
        // Extrai os dados corretos da resposta
        if (response.data.content) {
          this.notasCompleta = response.data.content;
          this.notas = response.data.content;
          this.totalPaginas = Math.ceil(response.data.content.length / this.tamanhoPagina);
        } else if (Array.isArray(response.data)) {
          this.notasCompleta = response.data;
          this.notas = response.data;
          this.totalPaginas = Math.ceil(response.data.length / this.tamanhoPagina);
        } else {
          this.notasCompleta = [];
          this.notas = [];
          this.totalPaginas = 1;
        }
      } catch (error) {
        console.error("Erro ao buscar notas fiscais:", error);
        this.$notify({
          group: "foo",
          title: "Erro",
          text: "Erro ao buscar notas fiscais",
          type: "error"
        });
        this.notasCompleta = [];
        this.notas = [];
        this.totalPaginas = 1;
      } finally {
        this.carregando = false;
      }
    },
    abrirConfirmacao(nota) {
      this.notaSelecionada = nota;
      this.modalExcluir = true;
    },
    async confirmarExclusao() {
      try {
        await EmissaoViagem.deletarNotaFiscal(this.notaSelecionada.id);
        this.modalExcluir = false;
        this.filtroChave = '';
        this.pagina = 1;
        this.buscarNotas();
      } catch (error) {
        console.error("Erro ao excluir nota fiscal:", error);
      }
    }
  }
};
</script>

<style scoped>
.my-5 {
  margin-top: 2rem;
  margin-bottom: 2rem;
}
</style>
